// Product Upsell Banner
// Displayed on product pages when the tenant lacks the required subscription.

import { Link } from 'react-router-dom';

interface ProductUpsellBannerProps {
  productId: 'fra' | 'ebm';
  productName: string;
  features: string[];
  isAdmin: boolean;
}

export function ProductUpsellBanner({ productId, productName, features, isAdmin }: ProductUpsellBannerProps) {
  return (
    <div className="mx-auto max-w-2xl py-12">
      <div className="rounded-xl border border-primary-200 bg-gradient-to-br from-primary-50 to-white p-8 text-center">
        {/* Product icon */}
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-100">
          {productId === 'fra' ? (
            <svg className="h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ) : (
            <svg className="h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          )}
        </div>

        <h2 className="text-xl font-bold text-gray-900">{productName}</h2>
        <p className="mt-2 text-gray-600">
          {productId === 'fra'
            ? 'Unlock powerful financial reporting capabilities for your organization'
            : 'Unlock powerful budgeting and planning capabilities for your organization'}
        </p>

        {/* Feature list */}
        <ul className="mt-6 inline-block space-y-3 text-left">
          {features.map((feature) => (
            <li key={feature} className="flex items-start gap-2.5 text-sm text-gray-700">
              <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {feature}
            </li>
          ))}
        </ul>

        {/* CTA */}
        <div className="mt-8">
          {isAdmin ? (
            <Link
              to="/settings/billing"
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 transition-colors"
            >
              Add to Plan
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          ) : (
            <p className="text-sm text-gray-500">
              Contact your organization admin to add this product to your plan
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
